/**
 * Änderungen anderer erscheinen von selbst (ADR 0029).
 *
 * Solange Netz da ist, hält die App eine Verbindung zu `/api/core/ereignisse`
 * offen. Der Server meldet dort nur „es gibt Neues“, und die App holt dann
 * denselben Stand wie beim gewöhnlichen Abgleich. Reißt die Verbindung ab,
 * versucht sie es mit wachsender Pause wieder; bis dahin gilt der Takt von
 * 60 Sekunden.
 *
 * Gelesen wird mit `fetch` und nicht mit `EventSource`: Nur so steht die
 * Anmeldung in der Kopfzeile und nicht in der Adresse, wo sie im Protokoll
 * des Apache landen würde.
 */

import { sitzungLesen } from "../api/klient.js";

export type Anstoss = "buchungen" | "stammdaten";

/**
 * Zerlegt, was bisher angekommen ist, in fertige Ereignisse. Liefert die
 * Ereignisse und den unvollständigen Rest, der auf mehr wartet.
 */
export function ereignisseLesen(puffer: string): { ereignisse: Anstoss[]; rest: string } {
  const bloecke = puffer.replaceAll("\r\n", "\n").split("\n\n");
  const rest = bloecke.pop() ?? "";
  const ereignisse: Anstoss[] = [];
  for (const block of bloecke) {
    for (const zeile of block.split("\n")) {
      if (!zeile.startsWith("event:")) continue;
      const art = zeile.slice("event:".length).trim();
      if (art === "buchungen" || art === "stammdaten") ereignisse.push(art);
    }
  }
  return { ereignisse, rest };
}

/** Die Pause vor dem nächsten Versuch: 2, 4, 8 … höchstens 60 Sekunden. */
export function pauseNach(fehlversuche: number): number {
  return Math.min(60_000, 2_000 * 2 ** Math.max(0, fehlversuche - 1));
}

let gestartet = false;

/** Startet die Verbindung. Ein zweiter Aufruf tut nichts. */
export function liveStarten(aufAnstoss: (art: Anstoss) => void): void {
  if (gestartet) return;
  gestartet = true;
  void schleife(aufAnstoss);
}

function warten(ms: number): Promise<void> {
  return new Promise((fertig) => setTimeout(fertig, ms));
}

/** Wartet, bis das Gerät wieder Netz meldet. */
function aufNetz(): Promise<void> {
  if (globalThis.navigator.onLine) return Promise.resolve();
  return new Promise((fertig) => {
    globalThis.addEventListener(
      "online",
      () => {
        fertig();
      },
      { once: true },
    );
  });
}

async function schleife(aufAnstoss: (art: Anstoss) => void): Promise<void> {
  let fehlversuche = 0;
  for (;;) {
    await aufNetz();
    const sitzung = sitzungLesen();
    if (sitzung === null) {
      // Abgemeldet: warten, bis wieder jemand angemeldet ist.
      await warten(pauseNach(fehlversuche + 3));
      continue;
    }

    const abbruch = new AbortController();
    const aus = (): void => {
      abbruch.abort();
    };
    globalThis.addEventListener("offline", aus, { once: true });
    try {
      const antwort = await fetch("/api/core/ereignisse", {
        headers: { authorization: `Bearer ${sitzung.token}`, accept: "text/event-stream" },
        signal: abbruch.signal,
        cache: "no-store",
      });
      if (!antwort.ok || antwort.body === null) throw new Error(String(antwort.status));
      fehlversuche = 0;
      // Nur fuer die Sitzung, mit der verbunden wurde: Nach dem Abmelden oder
      // einem Wechsel des Benutzers schliesst der Strom, statt einen Abgleich
      // ohne Anmeldung anzustossen.
      const weiterGueltig = (): boolean => sitzungLesen()?.token === sitzung.token;
      // Verbunden: Was waehrend der Pause geschah, jetzt holen.
      aufAnstoss("buchungen");

      const leser = antwort.body.getReader();
      const text = new TextDecoder();
      let puffer = "";
      for (;;) {
        const { done, value } = await leser.read();
        if (done) break;
        puffer += text.decode(value, { stream: true });
        if (!weiterGueltig()) {
          abbruch.abort();
          break;
        }
        const { ereignisse, rest } = ereignisseLesen(puffer);
        puffer = rest;
        for (const e of ereignisse) aufAnstoss(e);
      }
    } catch {
      // Kein Netz, Server neu gestartet, Proxy hat getrennt -- gleich noch einmal.
    } finally {
      globalThis.removeEventListener("offline", aus);
    }
    fehlversuche += 1;
    await warten(pauseNach(fehlversuche));
  }
}
